import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import logo from "../assets/SolidLogo1.svg";

const LINKS = [
  {
    label: "Visit Our Website",
    href: "/",
    icon: "language",
    internal: true,
  },
  {
    label: "Follow Us on Instagram",
    href: "https://instagram.com/solidfavours",
    icon: "photo_camera",
  },
];

const STORE_LINKS = [
  {
    label: "App Store",
    href: "https://apps.apple.com/app/id6760904836",
    customIcon: (
      <svg viewBox="0 0 384 512" fill="currentColor" className="w-6 h-6 text-black">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
      </svg>
    ),
  },
  {
    label: "Google Play",
    href: "https://play.google.com/store/apps/details?id=com.solidnl.app",
    icon: "android",
    iconColor: "text-[#01875f]",
  },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

function Links() {
  return (
    <div className="min-h-screen bg-surface flex flex-col items-center px-6 py-16 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary-container/10 blur-[160px] rounded-full" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary-container/15 blur-[120px] rounded-full" />

      <motion.div
        className="relative z-10 w-full max-w-md text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo & Brand */}
        <Link to="/" className="inline-flex flex-col items-center gap-3 mb-4">
          <img src={logo} alt="SOLID" className="h-16 w-auto" />
          <span className="text-3xl font-black tracking-tighter text-slate-900 font-headline">
            SOLID
          </span>
        </Link>

        <motion.p
          className="text-on-surface-variant text-base mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Solving inconveniences
        </motion.p>

        {/* Links */}
        <motion.div
          className="space-y-3"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <div className="flex gap-3 mt-1">
            {STORE_LINKS.map(({ label, href, icon, iconColor, customIcon }) => (
              <motion.div key={label} variants={itemVariants} className="flex-1">
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center aspect-square rounded-2xl text-on-surface bg-surface-container-low border border-outline-variant/20 hover:bg-surface-container hover:border-outline-variant/40 transition-all duration-200 active:scale-[0.98]"
                >
                  <div className="mb-1 flex items-center justify-center w-6 h-6">
                    {customIcon || (
                      <span className={`material-symbols-outlined text-2xl ${iconColor}`}>
                        {icon}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-bold">{label}</span>
                </a>
              </motion.div>
            ))}
          </div>
          
          {LINKS.map(({ label, href, icon, internal, highlight }) => {
            const className = `flex items-center gap-4 w-full px-6 py-4 rounded-2xl font-bold text-left transition-all duration-200 active:scale-[0.98] ${
              highlight
                ? "bg-[#1bc9d1] text-white hover:opacity-90 shadow-lg shadow-[#1bc9d1]/20"
                : "bg-surface-container-low border border-outline-variant/20 text-on-surface hover:bg-surface-container hover:border-outline-variant/40"
            }`;

            const content = (
              <>
                <span
                  className={`material-symbols-outlined text-xl ${
                    highlight ? "text-white" : "text-primary"
                  }`}
                >
                  {icon}
                </span>
                <span className="flex-1">{label}</span>
                <span
                  className={`material-symbols-outlined text-lg ${
                    highlight ? "text-white/70" : "text-on-surface-variant/50"
                  }`}
                >
                  {internal ? "arrow_forward" : "open_in_new"}
                </span>
              </>
            );

            return (
              <motion.div key={label} variants={itemVariants}>
                {internal ? (
                  <Link to={href} className={className}>
                    {content}
                  </Link>
                ) : (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {content}
                  </a>
                )}
              </motion.div>
            );
          })}


        </motion.div>

        {/* Footer */}
        <motion.p
          className="mt-12 text-sm text-on-surface-variant/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          &copy; 2026 SOLID. The Pristine Exchange.
        </motion.p>
      </motion.div>
    </div>
  );
}

export default Links;
